
import axios from 'axios'
import { ElMessage } from 'element-plus'
import type { UploadFile, UploadInstance } from 'element-plus'
import { computed, defineComponent, inject, onMounted, onUnmounted, ref } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { AuthPlugin } from '@/auth'
import {
  BreedingManager,
  channelTypeLabel,
  parsePredictionInspectResponse,
} from '@/types/breeding-manager'

const MAX_FILES = 10
const BREEDING_MANAGERS_PER_PAGE = 100
const CATTLE_ID_PATTERN = /^\d{10}$/

type RowStatus =
  | 'inspecting'
  | 'ready'
  | 'inspect_failed'
  | 'predicting'
  | 'succeeded'
  | 'failed'

interface BatchRow {
  uid: number
  name: string
  file: File
  status: RowStatus
  cattleId: string
  originalCattleId: string
  cattleIdOverridden: boolean
  farmer: string
  candidates: BreedingManager[]
  breedingManagerId: string | undefined
  autoSelected: boolean
  bms: number | null
  rank5Percentage: number | null
  predictionId: string | null
  predictionUri: string | null
  error: string
  trapeziusThickness: string
  trapeziusSubcutaneousFatThickness: string
  subcutaneousFatThickness: string
  latissimusThickness: string
}

const isZipFile = (file: File): boolean => {
  return file.name.toLowerCase().endsWith('.zip')
}

const errorMessage = (error: unknown, fallback: string): string => {
  const axiosError = error as {
    response?: { data?: { error?: string } }
    message?: string
  }
  return axiosError.response?.data?.error || axiosError.message || fallback
}

export default defineComponent({
  name: 'PredictionBatch',
  setup() {
    const router = useRouter()

    const backendUrl: string | undefined = inject('BackendUrl')
    if (!backendUrl) {
      throw new Error('BackendUrl is not provided')
    }

    const auth: AuthPlugin | undefined = inject('Auth')
    if (!auth) {
      throw new Error('Auth plugin is not provided')
    }

    const maxFiles = MAX_FILES
    const uploadRef = ref<UploadInstance>()
    const rows = ref<BatchRow[]>([])
    const allManagers = ref<BreedingManager[]>([])
    const isLoadingManagers = ref(false)
    const isPredicting = ref(false)

    const isInspecting = computed(() =>
      rows.value.some((row) => row.status === 'inspecting'),
    )
    const isBusy = computed(() => isPredicting.value || isInspecting.value)

    const pendingCount = computed(
      () => rows.value.filter((row) => row.status === 'ready' || row.status === 'failed').length,
    )
    const succeededCount = computed(
      () => rows.value.filter((row) => row.status === 'succeeded').length,
    )
    const failedCount = computed(
      () =>
        rows.value.filter(
          (row) => row.status === 'failed' || row.status === 'inspect_failed',
        ).length,
    )
    // 個体情報の取得が全件終わるまでは一括推定させない
    const canPredictAll = computed(() => pendingCount.value > 0 && !isBusy.value)

    const buildHeaders = async (multipart = false) => {
      const idToken = (await auth.getIdTokenClaims({}))?.__raw
      const impersonationToken = localStorage.getItem('impersonationToken')
      const headers: Record<string, string> = {
        Authorization: `Bearer ${idToken}`,
      }
      if (multipart) {
        headers['content-type'] = 'multipart/form-data'
      }
      if (impersonationToken) {
        headers['X-AIMEAT-Impersonation-Token'] = impersonationToken
      }
      return headers
    }

    const formatManagerLabel = (manager: BreedingManager) => {
      const typeLabel = channelTypeLabel[manager.channelType]
      const address = manager.addressShort ? ` / ${manager.addressShort}` : ''
      return `${manager.displayName}（${typeLabel}）${address}`
    }

    // 候補は絞り込み対象外で常に先頭に残し、その後ろに全件を並べる
    const managerOptions = (row: BatchRow): BreedingManager[] => {
      const byId = new Map<string, BreedingManager>()
      for (const candidate of row.candidates) {
        byId.set(candidate.id, candidate)
      }
      for (const manager of allManagers.value) {
        if (!byId.has(manager.id)) {
          byId.set(manager.id, manager)
        }
      }
      return Array.from(byId.values())
    }

    const statusLabel = (status: RowStatus): string => {
      if (status === 'inspecting') return '個体情報取得中'
      if (status === 'ready') return '推定待ち'
      if (status === 'inspect_failed') return '個体情報エラー'
      if (status === 'predicting') return '推定中'
      if (status === 'succeeded') return '完了'
      return '失敗'
    }

    const statusTagType = (status: RowStatus): string => {
      if (status === 'succeeded') return 'success'
      if (status === 'failed' || status === 'inspect_failed') return 'danger'
      if (status === 'ready') return 'info'
      return 'warning'
    }

    const loadAllBreedingManagers = async () => {
      isLoadingManagers.value = true
      try {
        const response = await axios.get(`${backendUrl}/v1/breeding-managers`, {
          headers: await buildHeaders(),
          params: { per_page: BREEDING_MANAGERS_PER_PAGE },
        })
        allManagers.value = response.data.data
      } finally {
        isLoadingManagers.value = false
      }
    }

    const applyInspectResult = (row: BatchRow, data: Parameters<typeof parsePredictionInspectResponse>[0]) => {
      const result = parsePredictionInspectResponse(data)
      row.cattleId = result.cattleId
      row.originalCattleId = result.originalCattleId
      row.cattleIdOverridden = result.cattleIdOverridden
      row.farmer = result.cattleSearch?.farmer || ''
      row.candidates = result.breedingCandidates
      // 候補1位を初期選択し、違う場合だけ人が直す運用にする
      const firstCandidateId = result.breedingCandidates[0]?.id
      if (firstCandidateId) {
        row.breedingManagerId = firstCandidateId
        row.autoSelected = true
      } else {
        row.breedingManagerId = undefined
        row.autoSelected = false
      }
      row.status = 'ready'
    }

    const inspectRow = async (row: BatchRow, cattleIdOverride?: string) => {
      row.status = 'inspecting'
      row.error = ''
      try {
        const data = new FormData()
        data.append('inspection', row.file)
        if (cattleIdOverride) {
          data.append('cattle_id', cattleIdOverride)
        }

        const response = await axios.post(
          `${backendUrl}/v1/prediction/inspect`,
          data,
          { headers: await buildHeaders(true) },
        )
        applyInspectResult(row, response.data)
      } catch (error) {
        row.status = 'inspect_failed'
        row.error = errorMessage(error, '個体情報の取得に失敗しました')
      }
    }

    // 複数ZIPは1リクエストにまとめ、サーバ側で飼養者取得を最大10件にする
    const inspectRows = async (targets: BatchRow[]) => {
      if (targets.length === 0) {
        return
      }
      for (const row of targets) {
        row.status = 'inspecting'
        row.error = ''
      }
      try {
        const data = new FormData()
        for (const row of targets) {
          data.append('inspection', row.file, row.name)
        }
        const response = await axios.post(
          `${backendUrl}/v1/prediction/inspect-batch`,
          data,
          { headers: await buildHeaders(true) },
        )
        const results = (response.data.results || []) as Array<
          Parameters<typeof parsePredictionInspectResponse>[0] & { error?: string | null }
        >
        targets.forEach((row, index) => {
          const item = results[index]
          if (!item || item.error) {
            row.status = 'inspect_failed'
            row.error = item?.error || '個体情報の取得に失敗しました'
            return
          }
          applyInspectResult(row, item)
        })
      } catch (error) {
        const message = errorMessage(error, '個体情報の取得に失敗しました')
        for (const row of targets) {
          row.status = 'inspect_failed'
          row.error = message
        }
      }
    }

    // on-change はファイルごとに発火するため、同じ選択操作を1回の取得にまとめる
    const pendingInspectUids = new Set<number>()
    let inspectTimer: ReturnType<typeof setTimeout> | null = null

    const scheduleInspect = (uids: number[]) => {
      for (const uid of uids) {
        pendingInspectUids.add(uid)
      }
      if (inspectTimer) {
        clearTimeout(inspectTimer)
      }
      inspectTimer = setTimeout(() => {
        inspectTimer = null
        const uidsToRun = Array.from(pendingInspectUids)
        pendingInspectUids.clear()
        const targets = rows.value.filter((row) => uidsToRun.includes(row.uid))
        inspectRows(targets)
      }, 50)
    }

    const reinspect = async (row: BatchRow) => {
      if (!CATTLE_ID_PATTERN.test(row.cattleId)) {
        row.error = '個体識別番号は10桁の数字で入力してください'
        return
      }
      await inspectRow(row, row.cattleId)
    }

    const createRow = (entry: UploadFile, file: File): BatchRow => ({
      uid: entry.uid,
      name: file.name,
      file,
      status: 'inspecting',
      cattleId: '',
      originalCattleId: '',
      cattleIdOverridden: false,
      farmer: '',
      candidates: [],
      breedingManagerId: undefined,
      autoSelected: false,
      bms: null,
      rank5Percentage: null,
      predictionId: null,
      predictionUri: null,
      error: '',
      trapeziusThickness: '',
      trapeziusSubcutaneousFatThickness: '',
      subcutaneousFatThickness: '',
      latissimusThickness: '',
    })

    const syncFiles = async (files: UploadFile[]) => {
      const previousByUid = new Map(rows.value.map((row) => [row.uid, row]))
      const next: BatchRow[] = []
      const addedUids = new Set<number>()
      const rejected: UploadFile[] = []

      for (const entry of files) {
        if (next.length >= maxFiles) {
          ElMessage.warning(`一度に処理できるファイル数は${maxFiles}件までです`)
          rejected.push(entry)
          continue
        }
        const raw = entry.raw
        if (!raw) {
          continue
        }
        if (!isZipFile(raw)) {
          ElMessage.warning(`${entry.name} は ZIP ではないため取り込みません`)
          rejected.push(entry)
          continue
        }

        const previous = previousByUid.get(entry.uid)
        if (previous) {
          next.push(previous)
        } else {
          next.push(createRow(entry, raw))
          addedUids.add(entry.uid)
        }
      }

      rows.value = next

      // 取り込まなかったファイルはアップロード欄にも残さない
      const upload = uploadRef.value
      if (upload) {
        for (const entry of rejected) {
          upload.handleRemove(entry)
        }
      }

      const added = rows.value.filter((row) => addedUids.has(row.uid))
      if (added.length > 0) {
        scheduleInspect(added.map((row) => row.uid))
      }
    }

    const handleFileChange = (_file: UploadFile, files: UploadFile[]) => {
      syncFiles(files)
    }

    const handleFileRemove = (_file: UploadFile, files: UploadFile[]) => {
      syncFiles(files)
    }

    const handleExceed = () => {
      ElMessage.warning(`一度に処理できるファイル数は${maxFiles}件までです`)
    }

    const saveThickness = async (row: BatchRow) => {
      const values = [
        row.trapeziusThickness,
        row.trapeziusSubcutaneousFatThickness,
        row.subcutaneousFatThickness,
        row.latissimusThickness,
      ]
      if (values.some((value) => value === '' || value == null)) {
        return
      }
      if (!row.predictionUri) {
        return
      }

      const headers: Record<string, string> = {
        ...(await buildHeaders()),
        'X-AIMEAT-PredictionUri': row.predictionUri,
      }
      await axios.get(`${backendUrl}/v1/example`, {
        headers,
        params: {
          bms: row.bms,
          tt: parseFloat(row.trapeziusThickness),
          tsft: parseFloat(row.trapeziusSubcutaneousFatThickness),
          sft: parseFloat(row.subcutaneousFatThickness),
          lt: parseFloat(row.latissimusThickness),
        },
      })
    }

    const predictRow = async (row: BatchRow) => {
      row.status = 'predicting'
      row.error = ''
      try {
        const data = new FormData()
        data.append('inspection', row.file)
        if (row.breedingManagerId) {
          data.append('breeding_manager_id', row.breedingManagerId)
        }
        if (row.cattleId) {
          data.append('cattle_id', row.cattleId)
        }
        data.append('remember_nlbc_mapping', 'true')

        const response = await axios.post(`${backendUrl}/v1/prediction`, data, {
          headers: await buildHeaders(true),
        })

        row.predictionId = response.data.id
        row.bms = response.data.BMS
        const bms8BelowProbability =
          Math.round(response.data.CORN.probabilities[4] * 10) * 10
        const rank5 = 100 - bms8BelowProbability
        row.rank5Percentage = Math.max(10, Math.min(90, rank5))
        row.predictionUri = response.headers['x-aimeat-predictionuri']

        await saveThickness(row)
        row.status = 'succeeded'
      } catch (error) {
        row.status = 'failed'
        row.error = errorMessage(error, '推定に失敗しました')
      }
    }

    const predictAll = async () => {
      if (!canPredictAll.value) {
        return
      }

      const targets = rows.value.filter(
        (row) => row.status === 'ready' || row.status === 'failed',
      )
      const unconfirmed = targets.filter((row) => !row.breedingManagerId).length
      if (unconfirmed > 0) {
        ElMessage.warning(
          `${unconfirmed}件は飼養管理者が未選択です。このまま推定を実行します。`,
        )
      }

      isPredicting.value = true
      try {
        // 1件ずつ順番に送り、途中で失敗しても残りを続行する
        for (const row of targets) {
          await predictRow(row)
        }

        if (failedCount.value === 0) {
          ElMessage.success('すべての推定が完了しました')
        } else {
          ElMessage.warning('一部の推定に失敗しました')
        }
      } finally {
        isPredicting.value = false
      }
    }

    // 入力途中の一覧を保持したいので、結果は別ウィンドウで開く
    const openDetail = (row: BatchRow) => {
      if (!row.predictionId) {
        return
      }
      const url = router.resolve(`/${row.predictionId}`).href
      window.open(url, '_blank', 'noopener')
    }

    const hasUnfinishedRows = computed(() =>
      rows.value.some((row) => row.status !== 'succeeded'),
    )

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnfinishedRows.value) {
        return
      }
      event.preventDefault()
      // Chrome は returnValue を設定しないと確認ダイアログを出さない
      event.returnValue = ''
    }

    onBeforeRouteLeave(() => {
      if (!hasUnfinishedRows.value) {
        return true
      }
      return window.confirm(
        '推定が完了していないZIPがあります。このページを離れると入力内容が失われます。移動しますか？',
      )
    })

    onMounted(() => {
      loadAllBreedingManagers()
      window.addEventListener('beforeunload', handleBeforeUnload)
    })

    onUnmounted(() => {
      if (inspectTimer) {
        clearTimeout(inspectTimer)
      }
      window.removeEventListener('beforeunload', handleBeforeUnload)
    })

    return {
      maxFiles,
      uploadRef,
      rows,
      isLoadingManagers,
      isPredicting,
      pendingCount,
      isInspecting,
      succeededCount,
      failedCount,
      canPredictAll,
      managerOptions,
      formatManagerLabel,
      statusLabel,
      statusTagType,
      handleFileChange,
      handleFileRemove,
      handleExceed,
      reinspect,
      predictAll,
      openDetail,
    }
  },
})
